import { Bell, Check } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button, Card } from '../../components/ui'
import { useToast } from '../../components/ui/toastContext'
import { NotificationRepository } from '../../data/local/notificationRepository'
import type { NotificationPreference } from '../../domain/notification'
import { detectRuntimePushCapability, isIosBrowser, requestBestEffortPush, type PushAttemptResult } from '../../services/push'
import { notificationRepository as defaultRepository, syncNow } from '../../services/syncRuntime'
import { sessionStore } from '../access/sessionStore'

export function NotificationReminder({
  variant = 'prompt',
  repository = defaultRepository,
  pushRequester = requestBestEffortPush,
}: {
  variant?: 'prompt' | 'settings'
  repository?: NotificationRepository
  pushRequester?: () => Promise<PushAttemptResult>
}) {
  const session = sessionStore((state) => state.session)
  const { showToast } = useToast()
  const [preference, setPreference] = useState<NotificationPreference | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const capability = detectRuntimePushCapability()

  useEffect(() => {
    if (!session) return
    void repository.getPreference(session.tripId, session.memberId).then(setPreference)
  }, [repository, session])

  if (!session || !preference) return null

  const hasAnswered = ['granted', 'denied', 'unavailable', 'configuration_missing'].includes(preference.pushStatus)
  const canPrompt = !hasAnswered && capability !== 'granted' && capability !== 'denied'
  if (variant === 'prompt' && !canPrompt) return null

  async function activate() {
    if (!session) return
    setBusy(true)
    setMessage(null)
    const result = await pushRequester()
    const nextPreference = await repository.setPushStatus(session.tripId, session.memberId, result.status)
    setPreference(nextPreference)
    if (navigator.onLine) void syncNow()
    if (result.status === 'granted') {
      showToast({ message: 'Rappels activés.', tone: 'success' })
    } else if (result.status === 'requires_install') {
      setMessage('Sur iPhone, ajoute d’abord Caravan à l’écran d’accueil.')
    } else if (result.status === 'denied') {
      setMessage('Pas de souci, Caravan ne te le redemandera pas.')
    } else {
      setMessage('Impossible de les activer ici pour le moment.')
    }
    setBusy(false)
  }

  if (variant === 'prompt') {
    return (
      <Card className="notification-reminder notification-reminder--prompt">
        <span aria-hidden="true"><Bell size={20} /></span>
        <div><strong>Active les rappels</strong><small>Pour les défis, les votes et le rendez-vous de 22 h.</small></div>
        <Button disabled={busy} variant="secondary" onClick={() => void activate()}>{busy ? 'Activation…' : 'Activer'}</Button>
        {capability === 'requires_install' || message ? <p>{message ?? 'Sur iPhone, ajoute d’abord Caravan à l’écran d’accueil.'}</p> : null}
      </Card>
    )
  }

  const active = preference.pushStatus === 'granted' || capability === 'granted'
  return (
    <Card className="notification-reminder notification-reminder--settings">
      <span aria-hidden="true">{active ? <Check size={20} /> : <Bell size={20} />}</span>
      <div>
        <h2>Rappels</h2>
        <p>{active ? 'Les rappels sont activés.' : 'Pour les défis, les votes et le rendez-vous de 22 h.'}</p>
        {isIosBrowser() && capability === 'requires_install' ? <small>Ajoute d’abord Caravan à l’écran d’accueil de l’iPhone.</small> : null}
        {message ? <small role="status">{message}</small> : null}
      </div>
      {!active && preference.pushStatus !== 'denied' ? <Button disabled={busy} variant="secondary" onClick={() => void activate()}>{busy ? 'Activation…' : 'Activer'}</Button> : null}
    </Card>
  )
}
